<script setup>
// O sentimento do cliente na conversa (Neuxs::Ia::Sentimento no servidor). O
// nível atual vem na própria conversa (additional_attributes.neuxs_sentimento)
// e muda sozinho quando o servidor reanalisa (evento conversation.updated).
//
// - compacto: só o emoji, para o card da lista (ConversationCard.vue);
// - normal: o chip do cabeçalho (ConversationHeader.vue), que abre a linha do
//   tempo da conversa.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import messages from './i18n';
import { listaSentimentos } from './api';

const props = defineProps({
  conversa: { type: Object, required: true },
  compacto: { type: Boolean, default: false },
});

const { t } = useI18n({ messages });
const route = useRoute();

const EMOJI = {
  muito_satisfeito: '😄',
  satisfeito: '🙂',
  neutro: '😐',
  insatisfeito: '😟',
  irritado: '😡',
};
const SETA = { melhorando: '↗', piorando: '↘' };
// As classes ficam aqui, num .vue: o Tailwind não lê os .js desta pasta.
const CORES = {
  muito_satisfeito: 'bg-n-teal-3 text-n-teal-11',
  satisfeito: 'bg-n-teal-3 text-n-teal-11',
  neutro: 'bg-n-slate-3 text-n-slate-11',
  insatisfeito: 'bg-n-amber-3 text-n-amber-11',
  irritado: 'bg-n-ruby-3 text-n-ruby-11',
};

const atual = computed(() => {
  const dados = props.conversa?.additional_attributes?.neuxs_sentimento;
  return dados && EMOJI[dados.nivel] ? dados : null;
});
const nome = nivel => t(`NEUXS_SENTIMENTO.NIVEIS.${nivel}`);
const dica = computed(() => {
  if (!atual.value) return '';
  const { nivel, motivo } = atual.value;
  return motivo ? `${nome(nivel)}: ${motivo}` : nome(nivel);
});

const aberto = ref(false);
const carregando = ref(false);
const erro = ref(false);
const mudancas = ref([]);

const carrega = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await listaSentimentos(
      route.params.accountId,
      props.conversa.id
    );
    mudancas.value = data.mudancas || [];
  } catch {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

const alterna = () => {
  aberto.value = !aberto.value;
  if (aberto.value) carrega();
};

// Outra conversa no mesmo cabeçalho: fecha o histórico da anterior.
watch(
  () => props.conversa?.id,
  () => {
    aberto.value = false;
    mudancas.value = [];
  }
);
// Mudou com o histórico aberto: recarrega.
watch(
  () => atual.value?.nivel,
  () => {
    if (aberto.value) carrega();
  }
);

const hora = iso =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
</script>

<template>
  <span
    v-if="atual && compacto"
    :title="dica"
    class="inline-block align-middle text-sm leading-none ms-1 not-italic"
  >
    {{ EMOJI[atual.nivel] }}
  </span>
  <div
    v-else-if="atual"
    v-on-click-outside="() => (aberto = false)"
    class="relative"
  >
    <button
      type="button"
      :title="dica"
      class="inline-flex items-center gap-1 h-7 px-2 rounded-lg text-xs font-medium whitespace-nowrap"
      :class="CORES[atual.nivel]"
      @click="alterna"
    >
      <span class="text-sm leading-none">{{ EMOJI[atual.nivel] }}</span>
      <span>{{ nome(atual.nivel) }}</span>
      <span
        v-if="SETA[atual.tendencia]"
        :title="t(`NEUXS_SENTIMENTO.TENDENCIA.${atual.tendencia}`)"
      >
        {{ SETA[atual.tendencia] }}
      </span>
    </button>
    <div
      v-if="aberto"
      class="absolute z-50 top-full mt-1 ltr:right-0 rtl:left-0 w-72 p-3 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg text-sm"
    >
      <p class="m-0 font-medium text-n-slate-12">
        {{ t('NEUXS_SENTIMENTO.TITULO') }}
      </p>
      <p v-if="atual.motivo" class="mt-1 mb-0 text-n-slate-11">
        {{ EMOJI[atual.nivel] }} {{ atual.motivo }}
      </p>
      <p class="mt-3 mb-1 text-xs uppercase tracking-wide text-n-slate-10">
        {{ t('NEUXS_SENTIMENTO.HISTORICO') }}
      </p>
      <p v-if="carregando" class="m-0 text-n-slate-11">
        {{ t('NEUXS_SENTIMENTO.CARREGANDO') }}
      </p>
      <p v-else-if="erro" class="m-0 text-n-ruby-11">
        {{ t('NEUXS_SENTIMENTO.ERRO') }}
      </p>
      <p v-else-if="!mudancas.length" class="m-0 text-n-slate-11">
        {{ t('NEUXS_SENTIMENTO.SEM_HISTORICO') }}
      </p>
      <ol v-else class="m-0 p-0 list-none grid gap-2 max-h-64 overflow-y-auto">
        <li
          v-for="(mudanca, indice) in mudancas"
          :key="indice"
          class="grid grid-cols-[1.5rem_1fr] gap-x-2"
        >
          <span class="text-base leading-5">{{ EMOJI[mudanca.nivel] }}</span>
          <span class="min-w-0">
            <span class="text-n-slate-12">{{ nome(mudanca.nivel) }}</span>
            <span class="text-n-slate-10 text-xs">
              · {{ hora(mudanca.em) }} ·
              {{ t(`NEUXS_SENTIMENTO.QUEM.${mudanca.quem}`) }}
            </span>
            <span
              v-if="mudanca.motivo"
              class="block text-xs text-n-slate-11 break-words"
            >
              {{ mudanca.motivo }}
            </span>
          </span>
        </li>
      </ol>
      <p class="mt-3 mb-0 text-xs text-n-slate-10">
        {{ t('NEUXS_SENTIMENTO.DICA') }}
      </p>
    </div>
  </div>
</template>
